import { TD, TH } from "./table";

import { useAppSelector, useObjectSelector } from "hooks/redux_hooks";
import { MonitoringEventStatsType } from "shared/types";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { isFutureClass, roundTo2Decimals } from "shared/utils/monitoring";
import { classroomSelectors } from "store";
import { selectBandwidthModes } from "store/selectors/classroom";
import { selectNAttendees } from "store/selectors/global";
import {
  selectAVRefreshStats,
  selectFailedPreClassCheckStats,
  selectMonitoringEventStatsByType,
  selectSupportActionStats,
} from "store/selectors/monitoring";

type ClassStatsPropsType = {
  intercomConversationCount: number;
};

function ClassStatsHeader() {
  return (
    <thead>
      <tr>
        <TH>Attendance</TH>
        <TH>Bug Reports</TH>
        <TH>Intercom Conversations</TH>
        <TH title="Number of page loads (enter-class MonitoringEvent) per participant per hour">
          Page Loads
        </TH>
        <TH title="Number of Test failures per participant per hour and percentage of users affected">
          Pre-class Check Failures
        </TH>
        <TH title="Number of AV (excluding av-service-requested and update-service-info) refreshes per participant per hour and percentage of users affected">
          AV Refreshes
        </TH>
        <TH title="Number of stream reloads per participant per hour and percentage of users affected">
          Stream Reloads
        </TH>
        <TH>Support Actions</TH>
        <TH title="Bandwidth modes (automatic, limited, audio-only and audio-only-forced )">
          BW Modes
        </TH>
        <TH title="Number of users with cameras enabled/disabled">Cameras</TH>
      </tr>
    </thead>
  );
}

function ClassStatsBody({ intercomConversationCount }: ClassStatsPropsType) {
  const classInfo = useAppSelector(classroomSelectors.selectClassInfo());
  const futureClass = isFutureClass(classInfo);
  const nAttendees = useAppSelector(selectNAttendees());
  const nonObserverCount = useAppSelector(classroomSelectors.selectNonObserverCount());
  const { count: bugReportCount } = useObjectSelector(
    selectMonitoringEventStatsByType(MonitoringEventTypeType.BUG_REPORT_SUBMITTED),
    ["count"],
  ) as MonitoringEventStatsType;
  const pageLoadStats = useObjectSelector(
    selectMonitoringEventStatsByType(MonitoringEventTypeType.ENTER_CLASS),
  ) as MonitoringEventStatsType;
  const preclassCheckFailStats = useObjectSelector(
    selectFailedPreClassCheckStats(),
  ) as MonitoringEventStatsType;
  const avRefreshStats = useObjectSelector(selectAVRefreshStats()) as MonitoringEventStatsType;
  const reloadStreamStats = useObjectSelector(
    selectMonitoringEventStatsByType(MonitoringEventTypeType.RELOAD_STREAM, true),
  ) as MonitoringEventStatsType;
  const supportActionStats = useObjectSelector(
    selectSupportActionStats(),
  ) as MonitoringEventStatsType;
  const bandwidthModes = useAppSelector(selectBandwidthModes());
  const cameras = useAppSelector(classroomSelectors.selectCameras());
  return (
    <tbody>
      <tr>
        <TD dataTestId="attendance">{`${nAttendees}/${nonObserverCount}`}</TD>
        <TD
          dataTestId="bug-report-count"
          conditionForDanger={bugReportCount >= 1}
        >{`${bugReportCount}`}</TD>
        <TD dataTestId="intercom-conversation-count">{`${intercomConversationCount}`}</TD>
        {!futureClass && pageLoadStats.mean ? (
          <TD
            dataTestId="page-load-stats"
            conditionForDanger={pageLoadStats.mean >= 2}
            conditionForWarning={pageLoadStats.mean >= 1.2}
          >
            {`${roundTo2Decimals(pageLoadStats.mean) || pageLoadStats.formattedMean}`}
          </TD>
        ) : (
          <TD dataTestId="page-load-stats">-</TD>
        )}
        {!futureClass && preclassCheckFailStats.mean ? (
          <TD
            dataTestId="preclass-check-fail-stats"
            conditionForDanger={
              preclassCheckFailStats.mean >= 0.4 ||
              (!!preclassCheckFailStats.percentAffectedUsers &&
                preclassCheckFailStats.percentAffectedUsers >= 30 &&
                preclassCheckFailStats.nAffectedUsers > 3)
            }
            conditionForWarning={
              preclassCheckFailStats.mean >= 0.25 ||
              (!!preclassCheckFailStats.percentAffectedUsers &&
                preclassCheckFailStats.percentAffectedUsers >= 20)
            }
          >
            {preclassCheckFailStats.formattedMean}
          </TD>
        ) : (
          <TD dataTestId="preclass-check-fail-stats">-</TD>
        )}
        {!futureClass && avRefreshStats.mean ? (
          <TD
            dataTestId="av-refresh-stats"
            conditionForDanger={
              avRefreshStats.mean >= 0.6 ||
              (!!avRefreshStats.percentAffectedUsers &&
                avRefreshStats.percentAffectedUsers >= 30 &&
                avRefreshStats.nAffectedUsers > 3)
            }
            conditionForWarning={
              avRefreshStats.mean >= 0.3 ||
              (!!avRefreshStats.percentAffectedUsers && avRefreshStats.percentAffectedUsers >= 20)
            }
          >
            {avRefreshStats.formattedMean}
          </TD>
        ) : (
          <TD dataTestId="av-refresh-stats">-</TD>
        )}
        {!futureClass && reloadStreamStats.mean ? (
          <TD
            dataTestId="reload-stream-stats"
            conditionForDanger={
              reloadStreamStats.mean >= 1.25 ||
              (!!reloadStreamStats.percentAffectedUsers &&
                reloadStreamStats.percentAffectedUsers >= 50 &&
                reloadStreamStats.nAffectedUsers > 3)
            }
            conditionForWarning={
              reloadStreamStats.mean >= 0.5 ||
              (!!reloadStreamStats.percentAffectedUsers &&
                reloadStreamStats.percentAffectedUsers >= 40)
            }
          >
            {reloadStreamStats.formattedMean}
          </TD>
        ) : (
          <TD dataTestId="reload-stream-stats">-</TD>
        )}
        {!futureClass && supportActionStats.mean ? (
          <TD
            dataTestId="support-action-stats"
            conditionForDanger={
              supportActionStats.mean >= 0.5 ||
              (!!supportActionStats.percentAffectedUsers &&
                supportActionStats.percentAffectedUsers >= 20 &&
                supportActionStats.nAffectedUsers > 3)
            }
            conditionForWarning={
              supportActionStats.mean >= 0.25 ||
              (!!supportActionStats.percentAffectedUsers &&
                supportActionStats.percentAffectedUsers >= 10)
            }
          >
            {supportActionStats.formattedMean}
          </TD>
        ) : (
          <TD dataTestId="support-action-stats">-</TD>
        )}
        <TD dataTestId="bandwidth-modes">{bandwidthModes}</TD>
        <TD dataTestId="cameras">{!futureClass ? cameras : "0-0"}</TD>
      </tr>
    </tbody>
  );
}

function ClassStats({ intercomConversationCount }: ClassStatsPropsType) {
  return (
    <table
      className="fds-table table-hover"
      style={{ tableLayout: "auto" }}
      data-testid="class-stats-table"
    >
      <ClassStatsHeader />
      <ClassStatsBody intercomConversationCount={intercomConversationCount} />
    </table>
  );
}

export { ClassStatsHeader, ClassStatsBody, ClassStats };
